import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Lock, Eye, EyeOff, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PWD_RULES = [
  { test: (s) => s.length >= 8, label: "At least 8 characters" },
  { test: (s) => /[A-Z]/.test(s), label: "An uppercase letter" },
  { test: (s) => /[0-9]/.test(s), label: "A number" },
  { test: (s) => /[^A-Za-z0-9]/.test(s), label: "A symbol (!@#$…)" },
];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, fetchMe } = useAuth();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const allRulesPass = PWD_RULES.every((r) => r.test(pwd));
  const matches = pwd === confirm && pwd.length > 0;

  useEffect(() => {
    if (!token) toast.error("Missing reset token. Please request a new reset link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!allRulesPass) { toast.error("Password doesn't meet all rules."); return; }
    if (!matches) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      await apiClient.post("/auth/reset-password", { token, password: pwd });
      const u = await fetchMe();
      setUser?.(u);
      toast.success("Password reset — you're signed in.");
      setDone(true);
      setTimeout(() => navigate("/dashboard"), 1200);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not reset password.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App">
      <Header />
      <section className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid="reset-password-page">
        <Link to="/" className="text-sm text-[var(--ink-3)] hover:text-[var(--ink)]">← Back to home</Link>
        <div className="card-elevated p-7 mt-4">
          {done ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 mx-auto rounded-full bg-[var(--secondary-soft)] flex items-center justify-center mb-4">
                <Check size={26} className="text-[var(--secondary)]" strokeWidth={3} />
              </div>
              <h1 className="font-display text-2xl tracking-[-0.022em] font-semibold">Password updated</h1>
              <p className="text-sm text-[var(--ink-2)] mt-2">Sending you to your dashboard…</p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="kicker text-[var(--primary)]">Reset password</div>
              <h1 className="font-display text-3xl tracking-[-0.022em] mt-2 font-semibold">Choose a new password.</h1>
              <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">Pick something secure that you'll remember.</p>

              <div className="mt-6 space-y-3.5">
                <div>
                  <span className="text-xs font-semibold text-[var(--ink-2)] mb-1.5 block uppercase tracking-wider">New password</span>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
                    <input
                      type={show ? "text" : "password"}
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      autoComplete="new-password"
                      className="brut-input pl-10 pr-10"
                      placeholder="New password"
                      data-testid="reset-password-input"
                      required
                    />
                    <button type="button" onClick={() => setShow(!show)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-3)] hover:text-[var(--ink)]" tabIndex={-1}>
                      {show ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <ul className="grid grid-cols-2 gap-x-4 gap-y-1" data-testid="reset-password-rules">
                  {PWD_RULES.map((r) => {
                    const pass = r.test(pwd);
                    return (
                      <li key={r.label} className="flex items-center gap-1.5 text-[0.72rem]" style={{ color: pass ? "var(--secondary-dark)" : "var(--ink-3)" }}>
                        <Check size={12} strokeWidth={pass ? 3 : 2} style={{ opacity: pass ? 1 : 0.4 }} />
                        {r.label}
                      </li>
                    );
                  })}
                </ul>

                <div>
                  <span className="text-xs font-semibold text-[var(--ink-2)] mb-1.5 block uppercase tracking-wider">Confirm password</span>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
                    <input
                      type={show ? "text" : "password"}
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      autoComplete="new-password"
                      className="brut-input pl-10"
                      placeholder="Confirm new password"
                      data-testid="reset-password-confirm"
                      required
                    />
                  </div>
                  {confirm && !matches && (
                    <p className="text-xs text-[var(--error)] mt-1.5">Passwords don't match.</p>
                  )}
                </div>
              </div>

              <button type="submit" disabled={busy || !allRulesPass || !matches || !token} className="btn-duo w-full justify-center mt-6" data-testid="reset-password-submit">
                {busy ? <span className="spinner" /> : <>Update password <ArrowRight size={15} /></>}
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
